import { v3Url } from '@/utils/fetcher';
import { useAxios } from '../base/useAxios';
import { useOptimisticUpdates } from '../base/useOptimisticUpdates';
import { useRealTimeUpdates } from '../base/useRealTimeUpdates';
import useTranslations from '@/platform/composables/useTranslations';
import useStore from '@/platform/composables/useStore';

const { hasOwnProperty } = Object.prototype;

function createProjectBudgetExpensePayload(budgetExpense) {
  const budgetExpensePayload = {};

  // if (hasOwnProperty.call(budgetExpense, 'id')) {
  //   budgetExpensePayload.id = Number(budgetExpense.id);
  // }

  if (hasOwnProperty.call(budgetExpense, 'projectBugdetId')) {
    budgetExpensePayload.projectBudgetId = Number(budgetExpense.projectBudgetId);
  }

  if (hasOwnProperty.call(budgetExpense, 'cost')) {
    budgetExpensePayload.cost = Number(budgetExpense.cost);
  }

  if (hasOwnProperty.call(budgetExpense, 'description')) {
    budgetExpensePayload.description = budgetExpense.description;
  }

  return budgetExpensePayload;
}

export default function useProjectBudgetExpenseActions() {
  const api = useAxios();
  const { emit: _emitOptimisticUpdate } = useOptimisticUpdates();
  const { emit: _emitRealTimeUpdate, socketId } = useRealTimeUpdates();
  const $t = useTranslations();
  const store = useStore();

  const config = () => ({
    headers: {
      'Socket-ID': socketId.value,
      'Triggered-By': 'user',
      'Sent-By': 'composable',
    },
  });

  /**
   *
   * @param {Promise} promise
   * @param {'create'|'update'|'delete'} action
   * @param {object} budgetExpense
   */
  function emitOptimisticUpdate(promise, action, budgetExpense) {
    _emitOptimisticUpdate({
      promise,
      type: 'budgetExpense',
      action,
      budgetExpense,
    });
  }

  /**
   *
   * @param {'new'|'edited'|'deleted'} action
   * @param {object} budgetExpense
   * @param {object} oldBudgetExpense
   */
  function emitRealTimeUpdate(action, budgetExpense, oldBudgetExpense) {
    _emitRealTimeUpdate({
      type: 'budgetExpense',
      action,
      budgetExpenseId: budgetExpense.id ?? oldBudgetExpense.id,
      projectBudgetId: budgetExpense.projectBudgetId ?? oldBudgetExpense.projectBudgetId,
    });
  }

  /**
   * Update an existing project budget expense.
   * @param {Object} budgetExpense
   */
  function updateProjectBudgetExpense(budgetExpense, oldBudgetExpense) {
    const url = v3Url(`projects/budgets/expenses/${budgetExpense.id}`);
    const promise = api
      .patch(url, { budgetExpense: createProjectBudgetExpensePayload(budgetExpense) }, config())
      .then(({ data: { budgetExpense: updatedBudgetExpense } }) => {
        emitRealTimeUpdate('edited', updatedBudgetExpense, oldBudgetExpense);
        return updatedBudgetExpense;
      })
      .catch(() => {
        store.dispatch('notifications/flashes/error', $t('There was an error updating the expense'));
      });

    emitOptimisticUpdate(promise, 'update', budgetExpense);
    return promise;
  }
  /**
   * Deletes a single project budget expense.
   * @param {object} budgetExpense
   */
  function deleteProjectBudgetExpense(budgetExpense) {
    const url = v3Url(`projects/budgets/expenses/${budgetExpense.id}`);
    const promise = api
      .delete(url, null, config())
      .then(() => {
        emitRealTimeUpdate('deleted', budgetExpense);
      })
      .catch(() => {
        store.dispatch('notifications/flashes/error', $t('There was an error deleting the expense'));
      });
    emitOptimisticUpdate(promise, 'delete', budgetExpense);
    return promise;
  }

  return {
    updateProjectBudgetExpense,
    deleteProjectBudgetExpense,
  };
}
